import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Bell, BellRinging, EnvelopeSimple, CheckCircle, Eye, Trash, ArrowsClockwise,
  Warning, User, Clock
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState({ total: 0, unread: 0, overdue_unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [checking, setChecking] = useState(false);

  const fetchData = async () => {
    try {
      const [notifsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/summary`, { withCredentials: true })
      ]);
      setNotifications(notifsRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCheckOverdue = async () => {
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/notifications/check-overdue`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Erro ao verificar atrasos');
    } finally {
      setChecking(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}`, { read: true }, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const { data } = await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const filteredNotifications = filter === 'all'
    ? notifications
    : filter === 'unread'
      ? notifications.filter(n => !n.read)
      : notifications.filter(n => n.type === filter);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Notificações</h1>
            <p className="text-[#73706A] mt-1">Alertas de mensalidades em atraso e avisos</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleCheckOverdue}
              disabled={checking}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="check-overdue-btn"
            >
              {checking ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <ArrowsClockwise size={18} className="mr-2" />
              )}
              Verificar Atrasos
            </Button>
            {summary.unread > 0 && (
              <Button
                onClick={handleMarkAllRead}
                variant="outline"
                data-testid="mark-all-read-btn"
              >
                <CheckCircle size={18} className="mr-2" />
                Marcar todas como lidas
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Bell size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Total</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{summary.total}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E85D04] rounded-lg flex items-center justify-center">
                <BellRinging size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Não Lidas</p>
                <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{summary.unread}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#9B2226] rounded-lg flex items-center justify-center">
                <Warning size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-[#73706A]">Atrasos Pendentes</p>
                <p className="text-2xl font-semibold text-[#9B2226] font-['Outfit']">{summary.overdue_unread}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#73706A]">Filtrar:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="notification-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="unread">Não lidas</SelectItem>
              <SelectItem value="overdue_payment">Mensalidades em atraso</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notification List */}
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-12 text-center">
              <Bell size={48} className="text-[#E6E2D8] mx-auto mb-4" />
              <p className="text-[#73706A]">
                {filter === 'unread' ? 'Nenhuma notificação não lida' : 'Nenhuma notificação encontrada'}
              </p>
              <p className="text-sm text-[#73706A] mt-2">
                Clique em "Verificar Atrasos" para checar mensalidades pendentes
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif, index) => (
              <div
                key={notif.id}
                className={`bg-white border rounded-lg p-5 transition-colors ${
                  notif.read ? 'border-[#E6E2D8]' : 'border-[#E85D04] border-l-4'
                }`}
                data-testid={`notification-${index}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notif.type === 'overdue_payment' ? 'bg-[#9B2226]' : 'bg-[#0077B6]'
                    }`}>
                      {notif.type === 'overdue_payment' ? (
                        <Warning size={20} className="text-white" />
                      ) : (
                        <Bell size={20} className="text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${notif.read ? 'text-[#73706A]' : 'text-[#1A1A1A]'}`}>
                          {notif.title}
                        </h3>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-[#E85D04] rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-[#73706A] mb-2">{notif.message}</p>
                      <div className="flex items-center gap-4 text-xs text-[#73706A]">
                        <span className="flex items-center gap-1">
                          <User size={14} /> {notif.student_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <EnvelopeSimple size={14} /> {notif.student_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {new Date(notif.created_at).toLocaleDateString('pt-BR')}
                        </span>
                        <span className={`badge ${
                          notif.email_status === 'mock_sent' ? 'badge-warning' : 'badge-success'
                        }`}>
                          {notif.email_status === 'mock_sent' ? 'Email simulado' : 'Email enviado'}
                        </span>
                      </div>

                      {/* Mock Email Preview */}
                      <details className="mt-3">
                        <summary className="text-xs text-[#0077B6] cursor-pointer hover:text-[#03045E]">
                          Ver preview do email
                        </summary>
                        <div className="mt-2 p-3 bg-[#F5F2EB] rounded-lg text-sm">
                          <p className="text-xs text-[#73706A] mb-1"><strong>Para:</strong> {notif.email_to}</p>
                          <p className="text-xs text-[#73706A] mb-2"><strong>Assunto:</strong> {notif.email_subject}</p>
                          <pre className="whitespace-pre-wrap text-[#1A1A1A] text-xs font-['DM_Sans']">{notif.email_body}</pre>
                        </div>
                      </details>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notif.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(notif.id)}
                        className="text-[#0077B6] hover:bg-[#F5F2EB]"
                        title="Marcar como lida"
                        data-testid={`mark-read-${index}`}
                      >
                        <Eye size={18} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
